import React, { useEffect, useRef, useState } from 'react';
import { getPreference, setPreference } from '../../helpers/preferencesHelpers';
import { buildCheckMenuItems } from '../../helpers/contextMenuHelpers';
import ContextMenu from './ContextMenu';
import Modal from './Modal';
import { toggleShrinkForDoc, isShrinkEnabled } from '../../helpers/uiHelpers';
import { getDefaultRenameMode, getRenameModePreferenceKey, normalizeRenameMode } from '../../helpers/renameHelpers';
import Icon from '../Icon';

export default function ModalSettings({ isOpen, onClose, user, activeTabId, tabs, hideSettings = [] }) {
  const [themeMode, setThemeMode] = useState('light');
  const [openDropdown, setOpenDropdown] = useState(null);
  const [fitScreenActive, setFitScreenActive] = useState(false);

  const normalizeOpenTarget = (value) => {
    if (value === 'opposite') return 'opposite';
    if (value === 'left') return 'left';
    if (value === 'right') return 'right';
    return 'focused';
  };

  const normalizeTabOpenMode = (value) => {
    if (value === 'replaceAnyType') return 'replaceAnyType';
    if (value === 'newTab') return 'newTab';
    return 'replaceSameType';
  };

  const [docOpenTarget, setDocOpenTarget] = useState(() => {
    try {
      return normalizeOpenTarget(getPreference(null, 'navOpenTargetDocuments', 'left'));
    } catch (err) {
      return 'left';
    }
  });

  const [versionOpenTarget, setVersionOpenTarget] = useState(() => {
    try {
      return normalizeOpenTarget(getPreference(null, 'navOpenTargetVersions', 'right'));
    } catch (err) {
      return 'right';
    }
  });

  const [chatOpenTarget, setChatOpenTarget] = useState(() => {
    try {
      return normalizeOpenTarget(getPreference(null, 'navOpenTargetChats', 'right'));
    } catch (err) {
      return 'right';
    }
  });

  const [tabOpenMode, setTabOpenMode] = useState(() => {
    try {
      return normalizeTabOpenMode(getPreference(null, 'navTabOpenMode', 'newTab'));
    } catch (err) {
      return 'newTab';
    }
  });

  const [docRenameMode, setDocRenameMode] = useState(() => {
    try {
      return normalizeRenameMode(getPreference(null, getRenameModePreferenceKey(null), getDefaultRenameMode(null)), null);
    } catch (err) {
      return getDefaultRenameMode(null);
    }
  });

  const [chatRenameMode, setChatRenameMode] = useState(() => {
    try {
      return normalizeRenameMode(getPreference(null, getRenameModePreferenceKey('chat'), getDefaultRenameMode('chat')), 'chat');
    } catch (err) {
      return getDefaultRenameMode('chat');
    }
  });

  const themeButtonRef = useRef(null);
  const docsButtonRef = useRef(null);
  const versionsButtonRef = useRef(null);
  const chatsButtonRef = useRef(null);
  const docRenameButtonRef = useRef(null);
  const chatRenameButtonRef = useRef(null);

  const firstThemePersistRef = useRef(true);

  const normalizeThemeMode = (value) => {
    if (value === 'night') return 'night';
    if (value === 'system') return 'system';
    return 'light';
  };

  useEffect(() => {
    try {
      const perUser = getPreference(user?.id ?? null, 'theme', null);
      const stored = perUser === null ? getPreference(null, 'theme', '') : perUser;
      setThemeMode(normalizeThemeMode(stored));
    } catch (e) {
      // ignore
    }
  }, [user]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined' || !document.body) return;

    const applyNight = (isNight) => {
      if (isNight) document.body.setAttribute('data-theme', 'night');
      else document.body.removeAttribute('data-theme');
    };

    let mql = null;
    let mqlHandler = null;

    if (themeMode === 'night') {
      applyNight(true);
    } else if (themeMode === 'system') {
      mql = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;
      applyNight(!!mql?.matches);
      mqlHandler = (event) => applyNight(!!event?.matches);
      if (mql && typeof mql.addEventListener === 'function') {
        mql.addEventListener('change', mqlHandler);
      } else if (mql && typeof mql.addListener === 'function') {
        mql.addListener(mqlHandler);
      }
    } else {
      applyNight(false);
    }

    if (firstThemePersistRef.current) {
      firstThemePersistRef.current = false;
      return () => {
        if (mql && mqlHandler && typeof mql.removeEventListener === 'function') {
          mql.removeEventListener('change', mqlHandler);
        } else if (mql && mqlHandler && typeof mql.removeListener === 'function') {
          mql.removeListener(mqlHandler);
        }
      };
    }

    try {
      const storedValue = themeMode === 'light' ? '' : themeMode;
      setPreference(user?.id ?? null, 'theme', storedValue);
      setPreference(null, 'theme', storedValue);
    } catch (e) {
      // ignore
    }

    return () => {
      if (mql && mqlHandler && typeof mql.removeEventListener === 'function') {
        mql.removeEventListener('change', mqlHandler);
      } else if (mql && mqlHandler && typeof mql.removeListener === 'function') {
        mql.removeListener(mqlHandler);
      }
    };
  }, [themeMode, user]);

  useEffect(() => {
    if (!isOpen) {
      setOpenDropdown(null);
    }
  }, [isOpen]);

  useEffect(() => {
    try {
      setPreference(null, 'navOpenTargetDocuments', docOpenTarget);
    } catch (err) {}
  }, [docOpenTarget]);

  useEffect(() => {
    try {
      setPreference(null, 'navOpenTargetVersions', versionOpenTarget);
    } catch (err) {}
  }, [versionOpenTarget]);

  useEffect(() => {
    try {
      setPreference(null, 'navOpenTargetChats', chatOpenTarget);
    } catch (err) {}
  }, [chatOpenTarget]);

  useEffect(() => {
    try {
      setPreference(null, 'navTabOpenMode', tabOpenMode);
    } catch (err) {}
  }, [tabOpenMode]);

  useEffect(() => {
    try {
      setPreference(null, getRenameModePreferenceKey(null), docRenameMode);
      window.dispatchEvent(new CustomEvent('rename-mode-changed', { detail: { docType: null, mode: docRenameMode } }));
    } catch (err) {}
  }, [docRenameMode]);

  useEffect(() => {
    try {
      setPreference(null, getRenameModePreferenceKey('chat'), chatRenameMode);
      window.dispatchEvent(new CustomEvent('rename-mode-changed', { detail: { docType: 'chat', mode: chatRenameMode } }));
    } catch (err) {}
  }, [chatRenameMode]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  useEffect(() => {
    const activeTab = tabs?.find(t => t.id === activeTabId);
    const activeDocId = activeTab?.docId;
    if (activeDocId) {
      const enabled = isShrinkEnabled(activeDocId);
      setFitScreenActive(enabled);
    } else {
      setFitScreenActive(false);
    }

    const handler = (e) => {
      try {
        const { docId: evDocId, value } = e.detail || {};
        const currentTab = tabs?.find(t => t.id === activeTabId);
        const currentDocId = currentTab?.docId;
        if (evDocId === currentDocId) setFitScreenActive(!!value);
      } catch (err) {}
    };

    window.addEventListener('view:shrinkToggled', handler);
    return () => window.removeEventListener('view:shrinkToggled', handler);
  }, [activeTabId, tabs]);

  const isThemeHidden = hideSettings.includes('Theme') || hideSettings.includes('Night Mode');
  const isFitScreenHidden = hideSettings.includes('Fit Docs to Screen');

  const openTargetOptions = [
    { value: 'focused', label: 'Current Editor' },
    { value: 'opposite', label: 'Opposite Editor' },
    { value: 'left', label: 'Left Editor' },
    { value: 'right', label: 'Right Editor' }
  ];

  const getOpenTargetLabel = (value) => {
    return openTargetOptions.find((option) => option.value === value)?.label || 'Current Editor';
  };

  

  const themeLabel = themeMode === 'night' ? 'Night' : themeMode === 'system' ? 'System' : 'Light';

  const themeMenuItems = buildCheckMenuItems({
    options: [
      { value: 'light', label: 'Light' },
      { value: 'night', label: 'Night' },
      { value: 'system', label: 'System' }
    ],
    selectedValue: themeMode,
    onSelect: (value) => setThemeMode(value),
    onAfterSelect: () => setOpenDropdown(null),
  });

  const openTargetMenuItems = (selected, setSelected) => buildCheckMenuItems({
    options: openTargetOptions,
    selectedValue: selected,
    onSelect: (value) => setSelected(value),
    onAfterSelect: () => setOpenDropdown(null),
  });

  const renameModeOptions = [
    { value: 'auto', label: 'Auto' },
    { value: 'prompt', label: 'Prompt' },
    { value: 'off', label: 'Off' }
  ];

  const getRenameModeLabel = (value, docType = null) => {
    const normalized = normalizeRenameMode(value, docType);
    return renameModeOptions.find((option) => option.value === normalized)?.label || 'Prompt';
  };

  const renameModeMenuItems = (selected, setSelected, docType = null) => buildCheckMenuItems({
    options: renameModeOptions,
    selectedValue: normalizeRenameMode(selected, docType),
    onSelect: (value) => setSelected(normalizeRenameMode(value, docType)),
    onAfterSelect: () => setOpenDropdown(null),
  });

  

  // Derived toggle states for new UI
  const alwaysNewTab = tabOpenMode === 'newTab';
  const replaceSameTypeOnly = tabOpenMode === 'replaceSameType';

  const renderDropdownSettingRow = ({
    rowKey,
    label,
    valueLabel,
    items,
    buttonRef,
    ariaLabel,
    descriptor
  }) => {
    const isRowOpen = openDropdown === rowKey;
    const hasDescriptor = !!descriptor;
    return (
      <>
        <div className={`modalRow${hasDescriptor ? ' hasDescriptor' : ''}`}>
          <div className="modalRowContent">
            <div className="modalRowLabel">{label}</div>
            <div className="modalRowControl modalRowControlMenu">
              <button
                ref={buttonRef}
                type="button"
                className="caretTextButton"
                data-context-placement="bottom"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenDropdown(prev => (prev === rowKey ? null : rowKey));
                }}
                aria-label={ariaLabel || label}
              >
                {valueLabel}
                <Icon icon={isRowOpen ? 'caret-up' : 'caret-down'} className="icon caretIcon small" />
              </button>
              {isRowOpen && (
                <ContextMenu
                  items={items}
                  triggerRef={buttonRef}
                  onClose={() => setOpenDropdown(null)}
                  placement="bottom right"
                  menuClass="settingsMenuCtx"
                />
              )}
            </div>
          </div>
        </div>
        {hasDescriptor && (
          <div className="modalDescriptor">{descriptor}</div>
        )}
      </>
    );
  };

  const handleFitScreenToggle = () => {
    const activeTab = tabs?.find(t => t.id === activeTabId);
    const activeDocId = activeTab?.docId;
    if (activeDocId) {
      toggleShrinkForDoc(activeDocId);
    }
  };

  return (
    <Modal
      id="settingsModal"
      isOpen={isOpen}
      title="Settings"
      confirmText="Done"
      cancelText="Close"
      onConfirm={onClose}
      onCancel={onClose}
    >
        {/* Navigation moved to top */}
        <div className="modalSection" id="navigationSection">
          <div className="modalSectionTitle">Navigation</div>
          <div className="modalBlock">
            <div className="modalRow">
              <div className="modalRowLabel">Opening Items</div>
            </div>
            <div className="modalDescriptor">When creating or selecting an item from the left side panel, which editor should it open?</div>

            <div className="modalSubBlock">
              {renderDropdownSettingRow({
                rowKey: 'open-documents',
                label: 'Documents',
                valueLabel: getOpenTargetLabel(docOpenTarget),
                items: openTargetMenuItems(docOpenTarget, setDocOpenTarget),
                buttonRef: docsButtonRef,
                ariaLabel: 'Documents opening behavior'
              })}

              {renderDropdownSettingRow({
                rowKey: 'open-versions',
                label: 'Versions',
                valueLabel: getOpenTargetLabel(versionOpenTarget),
                items: openTargetMenuItems(versionOpenTarget, setVersionOpenTarget),
                buttonRef: versionsButtonRef,
                ariaLabel: 'Versions opening behavior'
              })}

              {renderDropdownSettingRow({
                rowKey: 'open-chats',
                label: 'Chats',
                valueLabel: getOpenTargetLabel(chatOpenTarget),
                items: openTargetMenuItems(chatOpenTarget, setChatOpenTarget),
                buttonRef: chatsButtonRef,
                ariaLabel: 'Chats opening behavior'
              })}
            </div>

            {/* Move tab toggles into Navigation section without dropdown indentation */}
            <div className="modalBlock">
              <div className="modalRow">
                <div className="modalRowContent">
                  <div className="modalRowLabel">Always open Items in a New Tab</div>
                  <div className="modalRowControl">
                    <button
                      type="button"
                      className={`toggleButton ${alwaysNewTab ? 'active' : ''}`}
                      onClick={() => {
                        if (alwaysNewTab) {
                          setTabOpenMode('replaceSameType');
                        } else {
                          setTabOpenMode('newTab');
                        }
                      }}
                      aria-pressed={alwaysNewTab}
                      aria-label="Always open items in new tab"
                    >
                        <span className="toggleButtonKnob">
                        <Icon icon={alwaysNewTab ? 'check' : 'x'} className="icon small" />
                      </span>
                    </button>
                  </div>
                </div>
              </div>
              <div className="modalDescriptor">When set inactive, documents will replace the current active document in the target Editor. This helps prevent accidentally closing a document, but can result in too many tabs being open at once if you do not manually close them.</div>
            </div>

            {!alwaysNewTab && (
              <div className="modalBlock">
                <div className="modalRow">
                  <div className="modalRowContent">
                    <div className="modalRowLabel">Only replace items of the same type</div>
                    <div className="modalRowControl">
                      <button
                        type="button"
                        className={`toggleButton ${replaceSameTypeOnly ? 'active' : ''}`}
                        onClick={() => {
                          if (replaceSameTypeOnly) {
                            setTabOpenMode('replaceAnyType');
                          } else {
                            setTabOpenMode('replaceSameType');
                          }
                        }}
                        aria-pressed={replaceSameTypeOnly}
                        aria-label="Allow replacing other item types"
                      >
                        <span className="toggleButtonKnob">
                          <Icon icon={replaceSameTypeOnly ? 'check' : 'x'} className="icon small" />
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="modalDescriptor">When set active, opening Docs, Chats or Versions from the left side panel will only replace an active item of the same type in the Editors. This helps prevent accidentally replacing a chat with a document, for example.</div>
              </div>
            )}

            <div className="modalBlock">
              <div className="modalRow">
                <div className="modalRowLabel">Rename Suggestions</div>
              </div>
              <div className="modalDescriptor">Control whether untitled docs and chats suggest or apply AI/header-based names automatically.</div>

              <div className="modalSubBlock">
                {renderDropdownSettingRow({
                  rowKey: 'rename-documents',
                  label: 'Documents',
                  valueLabel: getRenameModeLabel(docRenameMode, null),
                  items: renameModeMenuItems(docRenameMode, setDocRenameMode, null),
                  buttonRef: docRenameButtonRef,
                  ariaLabel: 'Document rename suggestion behavior'
                })}

                {renderDropdownSettingRow({
                  rowKey: 'rename-chats',
                  label: 'Chats',
                  valueLabel: getRenameModeLabel(chatRenameMode, 'chat'),
                  items: renameModeMenuItems(chatRenameMode, setChatRenameMode, 'chat'),
                  buttonRef: chatRenameButtonRef,
                  ariaLabel: 'Chat rename suggestion behavior'
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="modalSeparator" />

        <div className="modalSection" id="displaySection">
          <div className="modalSectionTitle">Display</div>
          {!isThemeHidden && (
            <div className="modalBlock">
              {renderDropdownSettingRow({
                rowKey: 'theme',
                label: 'Theme',
                valueLabel: themeLabel,
                items: themeMenuItems,
                buttonRef: themeButtonRef,
                ariaLabel: 'Theme'
              })}
            </div>
          )}

          {!isFitScreenHidden && (
            <div className="modalBlock">
              <div className="modalRow">
                <div className="modalRowLabel">Fit Docs to Screen</div>
                <button
                  type="button"
                  className={`toggleButton ${fitScreenActive ? 'active' : ''}`}
                  onClick={handleFitScreenToggle}
                  aria-pressed={fitScreenActive}
                  aria-label="Fit Docs to Screen"
                >
                          <span className="toggleButtonKnob">
                            <Icon icon={fitScreenActive ? 'check' : 'x'} className="icon small" />
                          </span>
                </button>
              </div>
              <div className="modalDescriptor">If active and the document is A4 or Letter, it will scale down on mobile view so the entire width of the document is on screen instead of overflowing the content to fit.</div>
            </div>
          )}

        </div>

        <div className="modalSeparator" />
    </Modal>
  );
}
